<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased ">Info</h2>
        <nav v-if="GlobalPolicy.edit()" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton @click="save" color="green">Save Changes</FormButton>
            </template>
        </nav>

    </div>
    <p class="text-sm text-bhav-cool-gray-500 bg-bhav-cool-gray-100 p-2 mt-4 rounded">
        <span class="fak fa-alert mr-2"></span>
        This data is inherited from Kova. Any changes made here will not update Kova, but will override those values.
    </p>
    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Name</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">{{ floorplan.meta?.info?.name }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Slug</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ floorplan.slug }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Marketing Headline</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ floorplan.meta?.info?.headline }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Property Description</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ floorplan.meta?.info?.description }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Price</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ priceRange }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Square Feet</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ sqftRange }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Bedrooms</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ bedRange }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Full Bathrooms</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ fullBathRange }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Half Bathrooms</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ halfBathRange }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Garages</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ garageRange }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Plan Website</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50"><a class="text-bhav-blue-500 hover:underline" :href="floorplan.meta.info.website" target="_blank" title="Visit this home's website">{{ floorplan.meta.info.website }}</a></dd>
    </dl>

    <form @submit="save" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-else>
        <legend class="sr-only">Custom Properties</legend>

        <label for="info.name" class="font-normal text-bhav-blue-300 py-5">Name</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5">
            <TextInput id="info.name" v-model="item.meta.info.name" maxlength="50" size="50" />
        </div>

        <label for="slug" class="font-normal text-bhav-blue-300 py-5">Slug</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5">
            <TextInput id="slug" v-model="item.slug" @keyup="validateSlug" maxlength="255" size="255" />
        </div>

        <label for="info.headline" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Marketing Headline</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <TextInput placeholder="Example: 'Luxury amenities and a central media room'" id="info.headline" v-model="item.meta.info.headline" maxlength="100" size="100" />
        </div>

        <label for="info.description" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Property Description</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <textarea class="bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-48" id="info.description" v-model="item.meta.info.description" maxlength="2000"></textarea>
        </div>

        <div class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Price</div>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 md:inline-flex items-center gap-4">
            <NumberField :max="item.meta.info.price.max"  v-model="item.meta.info.price.min" prefix="$" label="Min" aria-label="Minimum Price" />
            <NumberField max="9999999" v-model="item.meta.info.price.max" prefix="$" label="Max" aria-label="Maximum Price" />
        </div>

        <div class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Square Feet</div>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 md:inline-flex items-center gap-4">
            <NumberField width="w-14" :max="item.meta.info.sqft.max" v-model="item.meta.info.sqft.min" label="Min" aria-label="Minimum Square Feet" />
            <NumberField width="w-14" max="99999" v-model="item.meta.info.sqft.max" label="Max" aria-label="Maximum Square Feet" />
        </div>

        <div class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Bedrooms</div>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 md:inline-flex items-center gap-4">
            <NumberField width="w-10" :max="item.meta.info.bedrooms.max" v-model="item.meta.info.bedrooms.min" label="Min" aria-label="Minimum Bedrooms" />
            <NumberField width="w-10" max="10" v-model="item.meta.info.bedrooms.max" label="Max" aria-label="Maximum Bedrooms" />
        </div>

        <div class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Full Bathrooms</div>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 md:inline-flex items-center gap-4">
            <NumberField width="w-10" :max="item.meta.info.bathrooms.max.full" v-model="item.meta.info.bathrooms.min.full" label="Min" aria-label="Minimum Full Bathrooms" />
            <NumberField width="w-10" max="10" v-model="item.meta.info.bathrooms.max.full" label="Max" aria-label="Maximum Full Bathrooms" />
        </div>

        <div class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Half Bathrooms</div>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 md:inline-flex items-center gap-4">
            <NumberField width="w-10" :max="item.meta.info.bathrooms.max.half" v-model="item.meta.info.bathrooms.min.half" label="Min" aria-label="Minimum Half Bathrooms" />
            <NumberField width="w-10" max="10" v-model="item.meta.info.bathrooms.max.half" label="Max" aria-label="Maximum Half Bathrooms" />
        </div>

        <div class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Garages</div>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 md:inline-flex items-center gap-4">
            <NumberField width="w-12" step=".5" :max="item.meta.info.garages.max" v-model="item.meta.info.garages.min" label="Min" aria-label="Minimum Garages" />
            <NumberField width="w-12" step=".5" max="10" v-model="item.meta.info.garages.max" label="Max" aria-label="Maximum Garages" />
        </div>

        <label for="info.website" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Plan Website</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <URLField id="info.website" v-model="item.meta.info.website" width="w-full" />
        </div>

    </form>
       
</template>

<script>
import { formatToCurrency } from '@/helpers/numberHelpers';
import {NumberField, FormButton, SelectField, URLField, TextInput} from '@/components/form';
import {helpers} from '@/models/Floorplan';
import { GlobalPolicy } from '@/policies/global';

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            item: {},
            editing: false,
        }
    },
    methods: {
        edit() {
            this.editing = true;
            this.item = {...this.floorplan};
        },
        cancel() {
            this.editing = false;
        },
        save() {
            this.$emit('update', this.item);
            this.cancel();
        },
        validateSlug(slug) {
            let clean = slug.target.value.replace(/[^A-Za-z0-9-]/,'');
            slug.target.value = clean;
        },
        formatToCurrency
    },
    computed: {
        ...helpers
    },
    emits: [
        'update'
    ],
    props: {
        floorplan: {
            type: Object,
            required: true
        }
    },
    components: {
        FormButton,
        NumberField,
        SelectField,
        URLField,
        TextInput
    }
}
</script>@/components/form